import { useState } from "react";
import { AdminActionButton } from "./AdminActionButton";

/**
 * Ban and un-ban, beside Suspend but deliberately not the same control
 * (#305).
 *
 * SUSPEND removes the account. BAN blocks the person from reaching it and
 * leaves the account and its history intact. Different acts with different
 * remedies, and an admin choosing between them should be able to see that
 * they are choosing.
 *
 * The duration is an explicit field with "Permanent" as its own option,
 * never a blank that happens to mean forever. Both directions of that
 * mistake are bad: a default duration quietly expires bans nobody meant to
 * be temporary, and a blank meaning permanent makes the heaviest action
 * the easiest one to reach by accident.
 */
const DURATIONS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "Permanent", days: null },
];

export function BanControls({ user, activeBan, onBan, onUnban }) {
  const [reason, setReason] = useState("");
  const [choice, setChoice] = useState("30");

  if (activeBan) {
    return (
      <AdminActionButton
        label="Lift Ban"
        danger={false}
        confirmBody={
          <>
            <p>
              {user.email} will be able to sign in again. The ban stays on the
              record as lifted rather than being deleted, so it is still clear
              that it happened and was reconsidered.
            </p>
            {/* The mirror of the ban warning. A lifted ban with the identity
                still blocked looks, to the person, exactly like the ban
                still being in force -- and they would report it as one. */}
            <p style={{ borderLeft: "3px solid var(--color-warning)", paddingLeft: "var(--space-2)" }}>
              <strong>Also step 1 of 2.</strong> If you blocked{" "}
              <strong>{user.email}</strong> in the Entra admin center, unblock them
              there too — otherwise they still cannot sign in and it looks to them
              like the ban never lifted.
            </p>
          </>
        }
        onConfirm={onUnban}
      />
    );
  }

  const chosen = DURATIONS.find((d) => String(d.days) === choice);
  const days = chosen ? chosen.days : null;

  return (
    <div style={{ display: "grid", gap: "var(--space-2)", maxWidth: "26rem" }}>
      <label style={{ display: "grid", gap: 4 }}>
        <span>Reason (required)</span>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          placeholder="Why this account is being blocked"
          className="touch-target"
        />
      </label>

      <label style={{ display: "grid", gap: 4 }}>
        <span>Duration</span>
        <select className="touch-target" value={choice} onChange={(e) => setChoice(e.target.value)}>
          {DURATIONS.map((d) => (
            <option key={d.label} value={String(d.days)}>{d.label}</option>
          ))}
        </select>
      </label>

      <AdminActionButton
        label="Ban From Signing In"
        disabled={!reason.trim()}
        confirmBody={
          <>
            <p>
              {user.email} will be signed out immediately and unable to sign in
              {days ? ` for ${chosen.label.toLowerCase()}` : " until the ban is lifted"}.
              Their apiary and its history are kept. This is logged to the audit trail.
            </p>
            {/* Said HERE, at the moment of banning, because the button
                looks like it finished the job and does not (#305).
                Self-service sign-up is live, so a banned person who signs
                up again gets a brand-new Free account -- the app has never
                seen that identity and cannot know it is the same human.
                Blocking the identity in the tenant is what closes that,
                and only an operator can do it: the app deliberately holds
                no Graph write permission over the tenant. */}
            <p style={{ borderLeft: "3px solid var(--color-warning)", paddingLeft: "var(--space-2)" }}>
              <strong>This is step 1 of 2.</strong> It blocks this account. It does
              not stop them signing up again — self-service sign-up would hand
              them a brand-new empty account. To close that, block or delete{" "}
              <strong>{user.email}</strong> in the Entra admin center as well.
              See <code>docs/REMOVING_A_USER.md</code>.
            </p>
          </>
        }
        onConfirm={() => onBan(reason.trim(), days)}
      />
    </div>
  );
}
